<script setup lang="ts">
import { useStorage } from '@vueuse/core'
import { ZhDecisionActions, ZhDecisionButton, ZhDecisionLead, ZhDecisionSections, ZhDetailsFold } from '@zerohuman/ui'
import { computed, ref, watch } from 'vue'
import { type CeoQuestionAnswer, ceoQuestions, type GateDecision, type GateRow } from '@/api'
import CeoQuestionsForm from '@/components/CeoQuestionsForm.vue'
import GateChoiceForm from '@/components/GateChoiceForm.vue'
import GatePayloadEditor from '@/components/GatePayloadEditor.vue'
import { gateDecisionDraftKey } from '@/lib/gateDecisionDraft'
import { actionLead, askerLabel, choiceLead, questionLead } from '@/lib/gateLead'
import { textFieldsToEdit } from '@/lib/gatePayloadEdit'
import { gateDecisionSections, gateSummarySections } from '@/lib/gateSummary'

/**
 * Approve/Reject controls for a waiting gate, plus the run's summary of what the gate decides on, the copy
 * explaining what Approve does, and a note sent with the decision. A CEO question gate (`os.ask_ceo`) renders
 * `CeoQuestionsForm` instead: one reply per question against the CTO's recommendation, sent with the note.
 * A gate that offers choices (`gate.choices`) renders `GateChoiceForm` instead: what the decision is about,
 * then its own choices in place of Approve and Reject, with none of the copy about a stored payload.
 * When the task routes it, Request changes sends the work back with the note as feedback (never without one).
 * Shared by the run detail view's linked (timeline step) and unlinked (standalone card) gates, and by Blockers.
 * The optional `payload` slot renders between the copy and the note, for callers that show the raw payload inline.
 * The note is this component's own, persisted per gate in localStorage. Chat's composer is never it: a pending
 * gate must not take over, or hold up, the conversation around it.
 *
 * Both kinds lead with one line (#1760, `lib/gateLead`) and fold their reasoning under "Show details". An action
 * leads with the summary's first line and the recommendation it wrote, and folds the summary laid out under its
 * headings; what Approve runs (the payload copy, its editor) stays in plain view. Where to read the work itself is
 * the run's `previewUrl` (`GateCardLink`): this card names no vendor's tool or address (#589). A question leads with its summary line, or who is asking and how many questions; one that changes
 * nothing leads by saying so, in question words. A gate with choices leads with the summary of what it decides on and
 * what it suggests, and folds nothing: its sections are what the choice is made on. An action whose run wrote no
 * summary has no lead: one would have to be made up.
 */
const props = defineProps<{ gate: GateRow; busy: boolean }>()
const emit = defineEmits<{
    decide: [
        id: string,
        decision: GateDecision,
        note: string,
        answers?: CeoQuestionAnswer[],
        /** The whole edited payload, when the CEO reworded an editable field before approving (os#911). */
        edit?: Record<string, unknown>,
        /** The key of the choice the decision was made through, on a gate that offers choices. */
        choice?: string,
    ]
}>()
/**
 * The gate's payload as fields, when there is something to edit in it: a field the gate names editable, the OS's
 * own for its tool or its task's (os#911, #433), that the run wrote as text, so the editor draws an input for it
 * (`textFieldsToEdit`). A gate whose named fields are all numbers or lists, or missing from the payload, has no
 * input to offer, so it gets null too and never says "Edit what this sends". Null leaves the card exactly as it was
 * before editing existed — the raw payload the `payload` slot renders, and nothing to change.
 */
const editableFields = computed(() => props.gate.editableFields ?? [])
const payloadFields = computed(() => {
    const payload = props.gate.payload
    if (textFieldsToEdit(payload, editableFields.value).length === 0) {
        return null
    }
    return payload as Record<string, unknown>
})
/**
 * The edit to send with the decision, or undefined while the payload still reads exactly as the run wrote it:
 * approving something untouched must behave exactly as it did before editing existed, so it sends no edit.
 */
const edit = ref<Record<string, unknown> | undefined>(undefined)
function edited(payload: Record<string, unknown>): void {
    edit.value = JSON.stringify(payload) === JSON.stringify(props.gate.payload) ? undefined : payload
}
/**
 * An edit belongs to the gate it was made on. When this card moves to another gate, the editor starts over
 * (it is keyed on the gate) and the edit held for the last gate goes with it, so Approve never sends one
 * gate's rewording under another gate's id — even when the two drafts happen to read the same.
 */
watch(
    () => props.gate.id,
    () => {
        edit.value = undefined
    },
)
/** Approve what is on screen: the edit when there is one, and otherwise the decision exactly as it always was. */
function approve(): void {
    if (edit.value) {
        emit('decide', props.gate.id, 'approve', noteValue.value, undefined, edit.value)
        return
    }
    emit('decide', props.gate.id, 'approve', noteValue.value)
}
const noteValue = useStorage(() => gateDecisionDraftKey(props.gate.id), '', undefined, {
    flush: 'sync',
    writeDefaults: false,
    onError: () => {
        // Keep the field usable when browser storage is unavailable.
    },
})
const questions = computed(() => ceoQuestions(props.gate))
/** The run's own account of what this gate decides on. Above the payload: it is what the decision is made on. */
const summary = computed(() => props.gate.summary?.trim() || '')
/** The summary under its own headings, as the Gates cards lay it out; its raw text if nothing parses. */
const summarySections = computed(() => gateSummarySections(summary.value))
/** What a gate with choices is deciding about, laid out as its Gates card lays it out. */
const decisionSections = computed(() => gateDecisionSections(props.gate))
/** An action's one line: the summary's first line and the recommendation it wrote, when it wrote one. */
const lead = computed(() => actionLead(summarySections.value))
/** A gate with choices' one line: its summary, and what it suggests, from what it decides on. */
const choiceLine = computed(() => choiceLead(decisionSections.value))
/** A question's one line: what changes (nothing, when it changes nothing), its summary line, or who asks how many. */
const askLead = computed(() =>
    questionLead({
        sections: summarySections.value,
        asker: askerLabel(props.gate),
        count: questions.value?.length ?? 0,
        changesNothing: props.gate.changesNothing === true,
    }),
)
const noteLabel = computed(() => {
    if (questions.value !== null) {
        return 'Note (optional; the reason, if you Reject)'
    }
    return props.gate.requestChanges ? 'Note (required for Request changes: what must change)' : 'Note (optional)'
})
</script>

<template>
	<template v-if="questions !== null">
		<ZhDecisionLead :line="askLead" />
		<ZhDetailsFold v-if="summarySections.length">
			<ZhDecisionSections :sections="summarySections" />
		</ZhDetailsFold>
		<CeoQuestionsForm
			:gate="gate"
			:questions="questions"
			:busy="busy"
			@submit="answers => emit('decide', gate.id, 'approve', noteValue, answers)"
			@reject="emit('decide', gate.id, 'reject', noteValue)"
		>
			<template #note>
				<label class="block space-y-1 text-sm">
					<span class="text-muted-foreground">{{ noteLabel }}</span>
					<textarea
						v-model="noteValue"
						rows="2"
						class="border-input w-full rounded-md border bg-transparent p-2 text-sm"
						:disabled="busy"
					/>
				</label>
			</template>
			<template v-if="$slots.link" #link><slot name="link" /></template>
		</CeoQuestionsForm>
	</template>
		<template v-else-if="gate.choices?.length">
			<!-- Its one line leads; nothing folds, as what the choice is made on is what it decides on (#1760). -->
			<ZhDecisionLead v-if="choiceLine" :line="choiceLine.line" :recommendation="choiceLine.recommendation" />
			<template v-if="decisionSections.length">
				<p class="text-sm font-medium">What needs deciding</p>
				<ZhDecisionSections :sections="decisionSections" />
			</template>
			<GateChoiceForm
				:gate="gate"
				:busy="busy"
				@decide="(id, decision, note, choice) => emit('decide', id, decision, note, undefined, undefined, choice)"
			>
				<template v-if="$slots.link" #link><slot name="link" /></template>
			</GateChoiceForm>
		</template>
		<template v-else>
		<template v-if="summary">
			<p class="text-sm font-medium">What you are approving</p>
			<ZhDecisionLead v-if="lead" :line="lead.line" :recommendation="lead.recommendation" />
			<p v-else class="text-sm whitespace-pre-wrap" data-lead>{{ summary }}</p>
			<!-- The reasoning: the summary under its headings. -->
			<ZhDetailsFold v-if="summarySections.length">
				<ZhDecisionSections :sections="summarySections" />
			</ZhDetailsFold>
		</template>
		<p v-if="payloadFields === null" class="text-muted-foreground text-sm">
			Exact stored payload. Approve executes this, not a new sample.
		</p>
		<template v-else>
			<p class="text-muted-foreground text-sm">
				Edit what this sends before you approve it. Approve executes exactly what is below.
			</p>
			<GatePayloadEditor
				:key="gate.id"
				:payload="payloadFields"
				:editable-fields="editableFields"
				@update:payload="edited"
			/>
		</template>
		<slot name="payload" />
		<label class="block space-y-1 text-sm">
			<span class="text-muted-foreground">{{ noteLabel }}</span>
			<textarea
				v-model="noteValue"
				rows="3"
				class="border-input w-full rounded-md border bg-transparent p-2 text-sm"
				:disabled="busy"
			/>
		</label>
		<!-- The row every decision card shares: Approve, Reject, Request changes, then the card's link. -->
		<ZhDecisionActions>
			<template #commands>
				<ZhDecisionButton :disabled="busy" @click="approve">Approve</ZhDecisionButton>
				<ZhDecisionButton tone="reject" :disabled="busy" @click="emit('decide', gate.id, 'reject', noteValue)">
					Reject
				</ZhDecisionButton>
				<ZhDecisionButton
					v-if="gate.requestChanges"
					tone="reject"
					:disabled="busy || !noteValue.trim()"
					@click="emit('decide', gate.id, 'request_changes', noteValue)"
				>
					Request changes
				</ZhDecisionButton>
			</template>
			<template v-if="$slots.link" #link><slot name="link" /></template>
		</ZhDecisionActions>
	</template>
</template>
